"use client";

import React, { createContext, useContext, useState, useCallback } from "react";

interface AuthTokens {
  accessToken: string | null;
  expiresAt: number | null;
}

interface AuthContextType {
  tokens: AuthTokens;
  accessToken: string | null;
  setTokens: (accessToken: string, expiresIn?: number) => void;
  clearTokens: () => void;
  isTokenExpired: () => boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AUTH_STORAGE_KEY = "rhuna_auth_tokens";
const EMPTY_TOKENS: AuthTokens = { accessToken: null, expiresAt: null };

const decodeTokenExpiresAt = (accessToken: string): number | null => {
  try {
    const payload = accessToken.split(".")[1];
    if (!payload) return null;

    const normalizedPayload = payload.replace(/-/g, "+").replace(/_/g, "/");
    const paddedPayload = normalizedPayload.padEnd(normalizedPayload.length + ((4 - (normalizedPayload.length % 4)) % 4), "=");
    const decoded = JSON.parse(window.atob(paddedPayload)) as { exp?: number };

    return decoded.exp ? decoded.exp * 1000 : null;
  } catch {
    return null;
  }
};

const getExpiresAt = (accessToken: string, expiresIn?: number): number => {
  if (expiresIn !== undefined) {
    return Date.now() + expiresIn * 1000;
  }

  return decodeTokenExpiresAt(accessToken) ?? Date.now() + 3600 * 1000;
};

const readStoredTokens = (): AuthTokens => {
  if (typeof window === "undefined") return EMPTY_TOKENS;

  try {
    const stored = window.sessionStorage.getItem(AUTH_STORAGE_KEY);
    if (!stored) return EMPTY_TOKENS;

    const parsed = JSON.parse(stored) as AuthTokens;
    if (!parsed.accessToken || !parsed.expiresAt || Date.now() >= parsed.expiresAt) {
      window.sessionStorage.removeItem(AUTH_STORAGE_KEY);
      return EMPTY_TOKENS;
    }

    return parsed;
  } catch {
    window.sessionStorage.removeItem(AUTH_STORAGE_KEY);
    return EMPTY_TOKENS;
  }
};

const persistTokens = (tokens: AuthTokens) => {
  if (typeof window === "undefined") return;

  try {
    if (!tokens.accessToken || !tokens.expiresAt) {
      window.sessionStorage.removeItem(AUTH_STORAGE_KEY);
      return;
    }

    window.sessionStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(tokens));
  } catch {
    // Storage can fail in private browsing or restricted environments.
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tokens, setTokensState] = useState<AuthTokens>(() => {
    const storedTokens = readStoredTokens();
    authTokensRef.current = storedTokens;
    return storedTokens;
  });

  const setTokens = useCallback((accessToken: string, expiresIn?: number) => {
    const nextTokens = { accessToken, expiresAt: getExpiresAt(accessToken, expiresIn) };
    setTokensState(nextTokens);
    authTokensRef.current = nextTokens;
    persistTokens(nextTokens);
  }, []);

  const clearTokens = useCallback(() => {
    setTokensState(EMPTY_TOKENS);
    authTokensRef.current = EMPTY_TOKENS;
    persistTokens(EMPTY_TOKENS);
  }, []);

  const isTokenExpired = useCallback(() => {
    return !tokens.expiresAt || Date.now() >= tokens.expiresAt;
  }, [tokens.expiresAt]);

  return (
    <AuthContext.Provider
      value={{
        tokens,
        accessToken: tokens.accessToken,
        setTokens,
        clearTokens,
        isTokenExpired,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// Alias for backward compatibility
export const useAuthContext = useAuth;

// Export tokens ref for external access
export const authTokensRef = {
  current: { accessToken: null as string | null, expiresAt: null as number | null },
};
